/**
 * Top-10 / Top-3 search visibility line chart for RankTab. Receives a pre-shaped
 * `series` (chronological points with `top10_pct`, `top3_pct`, `total`) — keeps
 * data shaping in the parent so this file stays purely presentational.
 */
import React from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ArrowUp, ArrowDown, LineChart as LineIcon } from "lucide-react";

function VisibilityTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl shadow-sm px-3 py-2 text-xs">
      <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 font-mono-tight">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-[#4a5568]">{p.name}</span>
          <span className="ml-auto text-[#1a202c] font-semibold">{p.value}%</span>
        </div>
      ))}
    </div>
  );
}

export default function VisibilityChart({ series }) {
  const current = series[series.length - 1];
  const prev = series[series.length - 2];
  const delta = current && prev ? current.top10_pct - prev.top10_pct : null;

  return (
    <div className="card-soft" data-testid="rank-visibility-chart">
      <div className="px-5 py-4 border-b border-[#edf2f7] flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
            <LineIcon className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
              Search visibility
            </div>
            <h3 className="font-display text-base mt-0.5 text-[#1a202c]">Tracked keywords in Top 10 / Top 3</h3>
          </div>
        </div>
        {current && (
          <div className="flex items-center gap-4 text-right">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-semibold">Top 10</div>
              <div className="font-mono-tight text-xl font-bold text-[#1a202c]">
                {current.top10_pct}%
                <span className="text-[10px] font-normal text-[#a0aec0] ml-1">
                  ({current.top10}/{current.total})
                </span>
              </div>
            </div>
            {delta != null && delta !== 0 && (
              <span
                className={`text-xs font-bold inline-flex items-center gap-0.5 ${
                  delta > 0 ? "text-[#38b2ac]" : "text-[#f56565]"
                }`}
                data-testid="rank-visibility-delta"
              >
                {delta > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                {Math.abs(delta)}%
              </span>
            )}
          </div>
        )}
      </div>
      {series.length === 0 ? (
        <div className="h-[220px] flex items-center justify-center text-xs text-[#a0aec0] font-mono-tight">
          No snapshots yet. Run a rank check or wait for the weekly cron.
        </div>
      ) : series.length === 1 ? (
        <div className="h-[220px] flex items-center justify-center text-xs text-[#a0aec0] font-mono-tight">
          Only one snapshot stored — chart will appear after the next run.
        </div>
      ) : (
        <div className="p-3 h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a0aec0" }} stroke="#cbd5e0" />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: "#a0aec0" }}
                stroke="#cbd5e0"
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip content={<VisibilityTooltip />} />
              <Line
                type="monotone"
                dataKey="top10_pct"
                name="Top 10"
                stroke="#38b2ac"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "#38b2ac" }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="top3_pct"
                name="Top 3"
                stroke="#6366f1"
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={{ r: 2.5, fill: "#6366f1" }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
